import {
  CellObject,
  Comments,
  Range,
  utils,
  WorkBook,
  WorkSheet,
  write,
  writeFile,
} from "xlsx-js-style";
import {
  MSExcelDownloadDto,
  MSExcelDownloadFeature,
  MSExcelDownloadSection,
  MSExcelDownloadSheetData,
  MSExcelRowData,
} from "@models/model-screener/entities/modelScreenerData";
import { generateSerialCode, getExcelRichText } from "@utils";
import { drawBorders } from "@pages/managed-accounts/components/buttons/export-excel/utils";
import {
  MSCellDataConstants,
  MSDefaultDateFormat,
  MSExcelDownloadConstants,
  MSExcelExportFileNameTemplate,
  MSExcelSectionNames,
  MSExportMessageTemplates,
  MSWorkBookNameByWorkSheetRecord,
  MSWorkSheetKey,
  MSWorkSheetStyleConstants,
} from "@pages/managed-accounts/model-screener/constant";
import { ModelScreenerFieldType } from "@models/model-screener/enums/fieldType";
import {
  fillBlue,
  fillLightBlue,
  fillLightYellow,
  fillOrange,
  fillYellow,
} from "@pages/managed-accounts/components/buttons/export-excel/config";
import { first, isEmpty } from "lodash";
import { format, parse } from "date-fns";
import JSZip from "jszip";
import { saveAs } from "file-saver";

class MSExcelDownloadStaticValues {
  adviserName: string = "";
  practiceName: string = "";
  downloaderName: string = "";
  downloaderFirmName: string = "";
  numOfColumnsRecord: Record<string, number> = {};
  endRowIndex: number = 8;
  workSheetRows: MSWorkSheetRows = [];
  serialCode: string = "";

  fillBlueRowIndexes: number[] = [];
  fillLightBlueRowIndexes: number[] = [];
  fillYellowRowIndexes: number[] = [];
  fillLightYellowRowIndexes: number[] = [];
}

type MSWorkSheetRow = (CellObject | string)[];
type MSWorkSheetRows = MSWorkSheetRow[];
type CellProcessorProps = {
  cellValue: string;
  additionalValue?: string | null;
  isPlatformAvailabilitySection?: boolean;
};

let staticValues = new MSExcelDownloadStaticValues();

const setStaticData = (data: MSExcelDownloadDto) => {
  staticValues.adviserName = data.adviserName;
  staticValues.practiceName = data.practiceName;
  staticValues.downloaderName = data.downloaderName;
  staticValues.downloaderFirmName = data.downloaderFirmName;
  staticValues.serialCode = generateSerialCode();

  data.sheets?.forEach((sheet) => {
    const firstSection = first(sheet.sections);

    if (!firstSection) return;

    staticValues.numOfColumnsRecord[sheet.id] =
      firstSection.rowData?.cellData?.length ?? 0;
  });
};

const resetRowsStaticData = () => {
  staticValues.endRowIndex = new MSExcelDownloadStaticValues().endRowIndex;
  staticValues.workSheetRows = new MSExcelDownloadStaticValues().workSheetRows;
  staticValues.fillBlueRowIndexes =
    new MSExcelDownloadStaticValues().fillBlueRowIndexes;
  staticValues.fillLightBlueRowIndexes =
    new MSExcelDownloadStaticValues().fillLightBlueRowIndexes;
  staticValues.fillYellowRowIndexes =
    new MSExcelDownloadStaticValues().fillYellowRowIndexes;
  staticValues.fillLightYellowRowIndexes =
    new MSExcelDownloadStaticValues().fillLightYellowRowIndexes;
};

const setEndRowIndex = () => {
  staticValues.endRowIndex = staticValues.workSheetRows.length - 1;
};

const insertFillBlueRowIndex = (index: number) => {
  staticValues.fillBlueRowIndexes.push(index);
};

const insertFillLightBlueRowIndex = (index: number) => {
  staticValues.fillLightBlueRowIndexes.push(index);
};

const insertFillYellowRowIndex = (index: number) => {
  staticValues.fillYellowRowIndexes.push(index);
};

const insertFillLightYellowRowIndex = (index: number) => {
  staticValues.fillLightYellowRowIndexes.push(index);
};

export const downloadMSExcels = async ({
  excelDataDto,
  separateFiles = true,
  zipIntoOneFile = true,
}: {
  excelDataDto: MSExcelDownloadDto;
  separateFiles?: boolean;
  zipIntoOneFile?: boolean;
}) => {
  setStaticData(excelDataDto);

  const workSheetDict: Record<string, WorkSheet> = getMSWorkSheets({
    data: excelDataDto,
  });
  const dictKeys = Object.keys(workSheetDict);
  const shouldZipFile = zipIntoOneFile && dictKeys.length > 1;
  const formatedDate = format(
    new Date(),
    MSExcelDownloadConstants.DateFormat,
  );

  if (separateFiles) {
    const files: Record<string, Uint8Array> = {};

    dictKeys?.forEach((key) => {
      const workBook: WorkBook = utils.book_new();
      const workSheet = workSheetDict[key];
      utils.book_append_sheet(workBook, workSheet, key);

      if (!shouldZipFile) {
        writeFile(
          workBook,
          MSExcelExportFileNameTemplate.ExcelFile({
            analysisName:
              MSWorkBookNameByWorkSheetRecord[key as MSWorkSheetKey],
            date: formatedDate,
          }),
        );
        return;
      }

      files[key] = write(workBook, { bookType: "xlsx", type: "array" });
    });

    if (!shouldZipFile) return;

    const zip = new JSZip();
    Object.keys(files).forEach((key) => {
      zip.file(
        MSExcelExportFileNameTemplate.ExcelFile({
          analysisName: MSWorkBookNameByWorkSheetRecord[key as MSWorkSheetKey],
          date: formatedDate,
        }),
        files[key],
      );
    });

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(
      zipBlob,
      MSExcelExportFileNameTemplate.ZipFile({
        date: formatedDate,
      }),
    );

    return;
  }

  // TODO: Implement the logic for combining all sheets into a single file
};

const getMSWorkSheets = ({
  data,
}: {
  data: MSExcelDownloadDto;
}): Record<string, WorkSheet> => {
  const workSheetDict: Record<string, WorkSheet> = {};

  data.sheets?.forEach((sheet) => {
    workSheetDict[sheet.name] = getMSWorkSheetData({ sheet });
    resetRowsStaticData();
  });

  return workSheetDict;
};

const getMSWorkSheetData = ({
  sheet,
}: {
  sheet: MSExcelDownloadSheetData;
}): WorkSheet => {
  const { workSheetRows } = staticValues;

  // static sections
  renderMSStaticSections({
    sheetId: sheet.id,
    sheetTitle: sheet.title,
  });

  // data sections
  renderDataSections({ sheet });

  // serial section
  renderSerialSection(sheet.id);

  // disclaimer section
  renderDisclaimerSection();

  const workSheet = utils.aoa_to_sheet(workSheetRows);
  addStylesToWorkSheet(workSheet, sheet.id);

  return workSheet;
};

const renderMSStaticSections = ({
  sheetId,
  sheetTitle,
}: {
  sheetId: string;
  sheetTitle: string;
}) => {
  const {
    adviserName,
    practiceName,
    downloaderFirmName,
    downloaderName,
    workSheetRows,
    numOfColumnsRecord,
  } = staticValues;
  const numOfColumns = numOfColumnsRecord[sheetId] ?? 1;

  workSheetRows.push([]);
  workSheetRows.push([
    "",
    {
      v: `SuitabilityHub managed account analysis: ${sheetId}`,
      t: "s",
      s: {
        font: {
          bold: true,
          sz: 14,
        },
      },
    },
  ]);
  insertFillBlueRowIndex(workSheetRows.length - 1);

  workSheetRows.push([
    "",
    {
      v:
        !isEmpty(adviserName) && !isEmpty(practiceName)
          ? `For ${adviserName} of ${practiceName} use only. Not to be disseminated`
          : `For ${downloaderFirmName} internal use only. Not to be disseminated`,
      t: "s",
      s: {
        alignment: { wrapText: numOfColumns === 1 },
      },
    },
  ]);

  workSheetRows.push(["", ""]);
  workSheetRows.push([
    "",
    "Owner:",
    {
      v: `${downloaderName} of ${downloaderFirmName}`,
      t: "s",
      s: {
        alignment: { horizontal: "left", wrapText: numOfColumns < 3 },
      },
    },
  ]);
  workSheetRows.push([
    "",
    "Downloaded on:",
    {
      v: new Date(),
      t: "d",
      z: "DD/MM/YY",
      s: {
        alignment: { horizontal: "left" },
      },
    },
  ]);
  workSheetRows.push(["", ""]);
  workSheetRows.push([
    "",
    {
      v: `${sheetTitle}`,
      t: "s",
      s: {
        font: {
          bold: true,
        },
      },
    },
  ]);
  insertFillBlueRowIndex(workSheetRows.length - 1);
};

const renderDataSections = ({ sheet }: { sheet: MSExcelDownloadSheetData }) => {
  const { workSheetRows } = staticValues;
  const { sections } = sheet;

  sections?.forEach((section) => {
    renderSection({ section, sheetRows: workSheetRows });
  });

  workSheetRows.push([]);
};

const renderSection = ({
  section,
  sheetRows,
}: {
  section: MSExcelDownloadSection;
  sheetRows: MSWorkSheetRows;
}) => {
  const { features, name, subSections, rowData, isDescriptionSection } =
    section;
  const isPlatformAvailabilitySection =
    name === MSExcelSectionNames.PlatformAvailability;

  const row: MSWorkSheetRow = [""];
  row.push(getHeaderCell({ title: section.name }));
  const cells = getRowData({
    rowData,
    isDescriptionSection: isDescriptionSection,
  });
  !!cells && row.push(...cells);

  isDescriptionSection
    ? insertFillLightBlueRowIndex(sheetRows.length)
    : insertFillYellowRowIndex(sheetRows.length);

  sheetRows.push(row);

  features?.forEach((feature) => {
    renderFeatures({ feature, sheetRows, isPlatformAvailabilitySection });
  });

  subSections?.forEach((subSection) => {
    insertFillLightYellowRowIndex(sheetRows.length);
    sheetRows.push([
      "",
      {
        v: subSection.name,
        t: "s",
        s: {
          font: {
            bold: true,
          },
        },
      },
    ]);

    subSection.features?.forEach((feature) => {
      renderFeatures({ feature, sheetRows });
    });
  });
};

const getHeaderCell = ({
  title,
  bold = true,
}: {
  title: string;
  bold?: boolean;
}): CellObject => ({
  v: title,
  t: "s",
  s: {
    font: {
      bold: bold,
    },
    alignment: {
      horizontal: "left",
      vertical: "center",
      wrapText: true,
    },
  },
});

const renderFeatures = ({
  feature,
  sheetRows,
  isPlatformAvailabilitySection = false,
}: {
  feature: MSExcelDownloadFeature;
  sheetRows: MSWorkSheetRows;
  isPlatformAvailabilitySection?: boolean;
}) => {
  const { rowData } = feature;

  const row: MSWorkSheetRow = [""];
  row.push(getHeaderCell({ title: feature.name, bold: false }));
  const cells = getRowData({ rowData, isPlatformAvailabilitySection });
  !!cells && row.push(...cells);

  sheetRows.push(row);
};

const getRowData = ({
  rowData,
  isDescriptionSection = false,
  isPlatformAvailabilitySection = false,
}: {
  rowData?: MSExcelRowData | null;
  isDescriptionSection?: boolean;
  isPlatformAvailabilitySection?: boolean;
}): CellObject[] | null => {
  if (!rowData) return null;

  const cells: CellObject[] = [];
  const cellProcessor = isDescriptionSection
    ? getManagerCell
    : GetCellDataFunctionMap[rowData.fieldType as ModelScreenerFieldType] ||
      getTextShortCell;

  rowData.cellData?.forEach((cell) => {
    const { value, additionalValue } = cell;
    const cellData = cellProcessor({
      cellValue: value,
      additionalValue,
      isPlatformAvailabilitySection,
    });
    cells.push(cellData);
  });

  return cells;
};

const getCellComments = (
  cellObject: CellObject,
  additionalValue?: string | null,
): CellObject => {
  const comments: Comments | undefined = additionalValue
    ? [
        {
          t: getExcelRichText({ htmlString: additionalValue }),
          a: "SuitabilityHub",
        },
      ]
    : undefined;
  if (comments) comments.hidden = true;

  const styles = cellObject.s;
  if (!!comments) styles.fill = fillOrange;

  cellObject.c = comments;
  cellObject.s = styles;

  return cellObject;
};

const getTextShortCell = ({ cellValue }: CellProcessorProps): CellObject => ({
  v: cellValue,
  t: "s",
  s: {
    alignment: { horizontal: "left", vertical: "center", wrapText: true },
    font: { sz: MSWorkSheetStyleConstants.DefaultFontSize },
  },
});

const getManagerCell = ({ cellValue }: CellProcessorProps): CellObject => {
  const basicCell = getTextShortCell({ cellValue });

  basicCell.s = {
    ...basicCell.s,
    alignment: { ...basicCell.s.alignment, horizontal: "center" },
    font: { bold: true },
  };

  return basicCell;
};

const getDateTimeCell = ({ cellValue }: CellProcessorProps): CellObject => {
  const basicCell = getTextShortCell({ cellValue });
  const parsedDate = parse(cellValue, MSDefaultDateFormat, new Date());

  if (isNaN(parsedDate.getTime())) return basicCell;

  basicCell.v = parsedDate;
  basicCell.t = "d";
  basicCell.z = MSDefaultDateFormat;

  return basicCell;
};

const getPercentageCell = ({
  cellValue,
  additionalValue,
}: CellProcessorProps): CellObject => {
  const basicCell = getTextShortCell({ cellValue });

  const floatValue = parseFloat(cellValue);
  const isValidNumber = !isNaN(floatValue);

  if (!isValidNumber) return basicCell;

  return getCellComments(
    {
      ...basicCell,
      v: floatValue / 100,
      t: "n",
      z: "0.00%",
    },
    additionalValue,
  );
};

const getYesNoCell = ({
  cellValue,
  additionalValue,
  isPlatformAvailabilitySection,
}: CellProcessorProps): CellObject => {
  const isEmptyAdditionalValue = isEmpty(additionalValue) || !additionalValue;
  const cellText = `${cellValue}${
    !isEmptyAdditionalValue
      ? `${!isPlatformAvailabilitySection ? "." : ","} ${getExcelRichText({
          htmlString: additionalValue,
        })}`
      : ""
  }`;

  const basicCell = getTextShortCell({ cellValue: cellText });
  if (!isEmptyAdditionalValue && !isPlatformAvailabilitySection) {
    basicCell.s = {
      ...basicCell.s,
      font: { sz: MSWorkSheetStyleConstants.RichTextFontSize },
    };
  }

  return basicCell;
};

const getTextLongCell = ({ cellValue }: CellProcessorProps): CellObject => {
  const richText = getExcelRichText({ htmlString: cellValue });
  const cell = getTextShortCell({ cellValue: richText });
  if (
    ![
      MSCellDataConstants.NoData,
      MSCellDataConstants.NAData,
      MSCellDataConstants.HiddenData,
    ].includes(richText)
  ) {
    cell.s = {
      ...cell.s,
      font: { sz: MSWorkSheetStyleConstants.RichTextFontSize },
    };
  }

  return cell;
};

const getNumberCell = ({
  cellValue,
  additionalValue,
}: CellProcessorProps): CellObject => {
  const basicCell = getTextShortCell({ cellValue });

  const numberValue = parseFloat(cellValue);
  const isValidNumber = !isNaN(numberValue);

  if (!isValidNumber) return basicCell;

  return getCellComments(
    {
      ...basicCell,
      v: numberValue,
      t: "n",
      z: "#,##0.00",
    },
    additionalValue,
  );
};

const getCurrencyCell = ({
  cellValue,
  additionalValue,
}: CellProcessorProps): CellObject => {
  const basicCell = getTextShortCell({ cellValue });

  const currencyValue = parseFloat(cellValue);
  const isValidNumber = !isNaN(currencyValue);

  if (!isValidNumber) return basicCell;

  return getCellComments(
    {
      ...basicCell,
      v: currencyValue,
      t: "n",
      z: "$#,##0;[Red]-$#,##0",
    },
    additionalValue,
  );
};

const GetCellDataFunctionMap: Record<
  ModelScreenerFieldType,
  (props: CellProcessorProps) => CellObject
> = {
  [ModelScreenerFieldType.Manager]: getManagerCell,
  [ModelScreenerFieldType.TextShort]: getTextShortCell,
  [ModelScreenerFieldType.DateTime]: getDateTimeCell,
  [ModelScreenerFieldType.Percentage]: getPercentageCell,
  [ModelScreenerFieldType.PercentageText]: getPercentageCell,
  [ModelScreenerFieldType.Currency]: getCurrencyCell,
  [ModelScreenerFieldType.CurrencyText]: getCurrencyCell,
  [ModelScreenerFieldType.Number]: getNumberCell,
  [ModelScreenerFieldType.NumberText]: getNumberCell,
  [ModelScreenerFieldType.TextLong]: getTextLongCell,
  [ModelScreenerFieldType.YesNo]: getYesNoCell,
  [ModelScreenerFieldType.Dropdown]: getTextShortCell,
  [ModelScreenerFieldType.Link]: getTextShortCell,
  [ModelScreenerFieldType.Image]: getTextShortCell,
};

const renderSerialSection = (sheetId: string) => {
  let { numOfColumnsRecord, workSheetRows, serialCode } = staticValues;
  const numOfColumns = numOfColumnsRecord[sheetId] ?? 1;

  workSheetRows.push([
    "",
    {
      v: MSExportMessageTemplates.SeeWebVersionForDetails,
      t: "s",
      s: {
        alignment: { horizontal: "left", wrapText: true },
      },
    },
    ...[
      ...Array(numOfColumns - 1).fill({
        v: "",
        t: "s",
      }),
    ],
    {
      v: `Serial: ${serialCode}`,
      t: "s",
      s: {
        alignment: { horizontal: "right" },
      },
    },
  ]);

  setEndRowIndex();
};

const renderDisclaimerSection = () => {
  const { workSheetRows } = staticValues;

  workSheetRows.push([]);
  workSheetRows.push([
    {
      v: "",
      t: "z",
    },
    {
      v: MSExportMessageTemplates.ExcelDownloadDisclaimer,
      t: "s",
      s: {
        alignment: { horizontal: "left", wrapText: true, vertical: "top" },
      },
    },
  ]);
};

const addStylesToWorkSheet = (workSheet: WorkSheet, sheetId: string) => {
  const {
    numOfColumnsRecord,
    endRowIndex,
    fillBlueRowIndexes,
    fillLightBlueRowIndexes,
    fillYellowRowIndexes,
    fillLightYellowRowIndexes,
  } = staticValues;
  const numOfColumns = numOfColumnsRecord[sheetId] ?? 1;

  // Fill row color
  const fillConfigs = [
    { indexes: fillBlueRowIndexes, fillStyle: fillBlue },
    { indexes: fillLightBlueRowIndexes, fillStyle: fillLightBlue },
    { indexes: fillYellowRowIndexes, fillStyle: fillYellow },
    { indexes: fillLightYellowRowIndexes, fillStyle: fillLightYellow },
  ];

  fillConfigs.forEach(({ indexes, fillStyle }) => {
    fillRowColor({ workSheet, indexes, fillStyle, sheetId });
  });

  // Set column width
  const maxColumns = Math.max(
    MSWorkSheetStyleConstants.DisclaimerSectionColSpan,
    numOfColumns + 1,
  );
  workSheet["!cols"] = [
    { wch: 2 },
    ...[
      ...Array(maxColumns).fill({
        wch: MSWorkSheetStyleConstants.DefaultColumnWidth,
      }),
    ],
  ];

  // Set row height
  workSheet["!rows"] = [
    { hpt: 18 },
    { hpt: MSWorkSheetStyleConstants.AnalysisNameRowHeight },
  ];

  // Merge cells
  const mergesRanges: Range[] = [
    {
      s: { r: 1, c: 1 },
      e: { r: 1, c: 1 + numOfColumns },
    },
    {
      s: { r: endRowIndex + 2, c: 1 },
      e: { r: endRowIndex + 17, c: 4 },
    },
  ];

  if (numOfColumns > 1) {
    mergesRanges.push({
      s: { r: endRowIndex, c: 1 },
      e: { r: endRowIndex, c: 2 },
    });
  }

  workSheet["!merges"] = mergesRanges;

  drawTableBorders(workSheet, sheetId);
};

const fillRowColor = ({
  workSheet,
  indexes,
  fillStyle,
  sheetId,
}: {
  workSheet: WorkSheet;
  indexes: number[];
  fillStyle: any;
  sheetId: string;
}) => {
  const { numOfColumnsRecord } = staticValues;
  const numOfColumns = numOfColumnsRecord[sheetId] ?? 1;

  indexes.forEach((i) => {
    for (let col = 1; col <= numOfColumns + 1; col++) {
      const address = utils.encode_cell({ c: col, r: i });
      const cell: CellObject = workSheet[address] || {
        v: "",
        t: "s",
      };
      cell.s = { ...cell.s, fill: fillStyle };

      workSheet[address] = cell;
    }
  });
};

const drawTableBorders = (workSheet: WorkSheet, sheetId: string) => {
  const { numOfColumnsRecord, endRowIndex } = staticValues;
  const numOfColumns = numOfColumnsRecord[sheetId] ?? 1;

  const rangeToDraw: Range = {
    s: {
      c: 0,
      r: 0,
    },
    e: {
      c: numOfColumns + 1,
      r: endRowIndex,
    },
  };

  drawBorders(workSheet, rangeToDraw);
};
